import { TypedUseQueryStateResult } from '@reduxjs/toolkit/query/react';
import {
  ScheduleIndexResponse,
  SummarizeMinimizedResponse,
} from '@/dashboard/services/dashboardApi';
import { EmployeesByPeriodEmployeeType } from '@/payroll-period/services/payrollPeriodApi';
import {
  GraphStatusesWithoutSubmitted,
  EmployeeByIdContributions,
  ValueOf,
} from '@/common/common.types';
import tokens from '@/payroll-period/tokens.json';
import webStorage, { LOCAL_STORAGE_KEYS } from '@/core/web-storage/webStorage';
import { reduceTokenString } from '@/common/utils/app.utils';

export const EMP_FILTER_FORM_NAME = 'pp-filter-employees-form';
export const RECORDS_PER_PAGE = 50;

type DeferredSchedulesResponse = TypedUseQueryStateResult<ScheduleIndexResponse, unknown, never>;
export type EmployeeRowStatusType = Exclude<GraphStatusesWithoutSubmitted, 'Warnings'>;
export type PayrollPeriodRouteLoaderValues = DeferredSchedulesResponse;
export type LoadedRouteLoaderValues = { data: ScheduleIndexResponse };
export type ErrorType = keyof typeof errorTypeValues;
export const errorTypeValues = {
  '-1': '\u00A0',
  Any: 'Any Error',
  Warning: 'Warnings',
  Error: 'Errors',
  Critical: 'Critical',
  DataWarehouse: 'Post Submit Errors',
};

export const getErrorTypeLabel = (errorType: string) => {
  if (errorType === '-1' || !errorType) {
    return '';
  }
  return errorTypeValues[errorType as keyof typeof errorTypeValues];
};

export function isValidYearMonthString(yearMonthString: string) {
  const yearMonthRegex = /^\d{4}(0[1-9]|1[0-2])$/;
  return yearMonthRegex.test(yearMonthString);
}

export function getReadableMonthYear(yearMonthString: string) {
  if (!isValidYearMonthString(yearMonthString)) {
    return yearMonthString;
  }

  const year = Number(yearMonthString.slice(0, 4));
  const month = Number(yearMonthString.slice(4, 6)) - 1;

  const date = new Date(year, month);
  const formattedDate = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return formattedDate;
}

export function getErrorsFromContributionRecord(contribution: EmployeeByIdContributions) {
  const preSubmitErrors = contribution.preSubmitErrors ?? [];
  return {
    contributionErrors: contribution.contributionErrors.map((error) => error.errorMessage),
    preSubmitErrors: preSubmitErrors
      .filter((error) => error.severity !== 'Warning')
      .map((error) => error.description),
    preSubmitWarnings: preSubmitErrors
      .filter((error) => error.severity === 'Warning')
      .map((error) => error.description),
  };
}

function combinedIsErrorFromContributions(employee: EmployeesByPeriodEmployeeType) {
  return employee.contributionError || employee.preSubmitError || employee.preSubmitCritical;
}

export function computeEmployeeStatus(
  employee: EmployeesByPeriodEmployeeType,
  isBypassed = false,
): Exclude<GraphStatusesWithoutSubmitted, 'Accepted'> | 'BypassedErrors' | 'None' {
  const isError = combinedIsErrorFromContributions(employee);
  const isCriticalError = employee.preSubmitCritical > 0 || employee.contributionError > 0;
  if (isError && !isCriticalError && (employee.latestBatchStatus === 'A' || isBypassed)) {
    return 'BypassedErrors';
  }

  if (isError) {
    return 'Errors';
  }

  const isWarning = employee.preSubmitWarning > 0;
  if (isWarning) {
    return 'Warnings';
  }

  const isUnsubmitted = employee.latestBatchStatus !== 'A';
  if (isUnsubmitted) {
    return 'Unsubmitted';
  }

  return 'None';
}

export function getSummarizedRecordStatus(
  employee: EmployeesByPeriodEmployeeType,
  counts: ValueOf<SummarizeMinimizedResponse>,
): GraphStatusesWithoutSubmitted | 'BypassedErrors' {
  const isBypassed = counts.bypassTimestamp !== null;
  const employeeStatus = computeEmployeeStatus(employee, isBypassed);

  if (
    employeeStatus === 'Errors' ||
    employeeStatus === 'Warnings' ||
    employeeStatus === 'Unsubmitted' ||
    employeeStatus === 'BypassedErrors'
  ) {
    return employeeStatus;
  }

  if (counts.batchError === false && counts.latestBatchStatus === 'A') {
    return 'Accepted';
  } else {
    return 'Unsubmitted';
  }
}

export function ifMonthParamsValid(year: string | undefined, month: string | undefined) {
  if (!year || !month) {
    return false;
  }

  const yearNum = Number(year);
  const monthNum = Number(month);

  if (isNaN(yearNum) || isNaN(monthNum)) {
    return false;
  }

  if (monthNum < 1 || monthNum > 12) {
    return false;
  }

  const period = `${year}${month}`;
  if (period.length !== 6) {
    return false;
  }

  return true;
}

export function getAddEmployeeButtonTitle(isActionButtonHidden: boolean, appStatusDone: boolean) {
  if (isActionButtonHidden) {
    if (!appStatusDone) {
      return tokens['PLEASE_WAIT_LIST'];
    } else {
      return tokens['ACCEPTED_BATCH_EMPLOYEE'];
    }
  }
  return tokens['ADD_EMPLOYEE'];
}

export function getExpandedPanelBorderType(
  postSubmitErrors: string[],
  preSubmitErrors: string[],
  preSubmitWarnings: string[],
) {
  if (postSubmitErrors.length > 0 || preSubmitErrors.length > 0) {
    return 'error';
  }
  if (preSubmitWarnings.length > 0) {
    return 'warning';
  }

  return 'default';
}

export function saveFilterToStorage(values: Record<string, string>) {
  webStorage.local.setItem(LOCAL_STORAGE_KEYS.PP_EMP_FILTER_FORM, values);
  return values;
}

export function saveFilterResultToStorage(values: number[]) {
  webStorage.local.setItem(LOCAL_STORAGE_KEYS.PP_EMP_FILTER_RESULT, values);
  return values;
}

export function getFilterResultFromStorage() {
  const value = webStorage.local.getItem(LOCAL_STORAGE_KEYS.PP_EMP_FILTER_RESULT);
  if (value && Array.isArray(value)) {
    return value as number[];
  }
  return [] as number[];
}

export function getFilterFromStorage() {
  const value = webStorage.local.getItem(LOCAL_STORAGE_KEYS.PP_EMP_FILTER_FORM);
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    return value as Record<string, string>;
  }
  return {} as Record<string, string>;
}

export function isEmployeeFilterSet(filters: Record<string, string> | undefined) {
  const result = filters === undefined ? getFilterFromStorage() : filters;
  return Object.keys(result).length !== 0;
}

export default function generateNoEmployeeMessage(
  filters: Record<string, string>,
  errorTypeFilter: ErrorType,
  isNoData: boolean | undefined,
) {
  const { NO_EMPLOYEE_FOUND_MESSAGES } = tokens;
  let message = NO_EMPLOYEE_FOUND_MESSAGES.DEFAULT;

  const isErrorTypeFilter = errorTypeFilter && errorTypeFilter !== '-1';
  const isFirstName = filters && filters['firstName'] && String(filters['firstName']).trim() !== '';
  const isLastName = filters && filters['lastName'] && String(filters['lastName']).trim() !== '';

  if (isErrorTypeFilter && (isFirstName || isLastName)) {
    const errorTypeFilterDisplayValue = getErrorTypeLabel(errorTypeFilter);
    message = reduceTokenString(
      NO_EMPLOYEE_FOUND_MESSAGES.WITH_ET_AND_NAME_FILTER,
      errorTypeFilterDisplayValue,
    );
  } else if (isErrorTypeFilter && !(isFirstName || isLastName)) {
    const errorTypeFilterDisplayValue = getErrorTypeLabel(errorTypeFilter);
    message = reduceTokenString(
      NO_EMPLOYEE_FOUND_MESSAGES.WITH_ONLY_FILTER,
      errorTypeFilterDisplayValue,
    );
  } else if (!isErrorTypeFilter && (isFirstName || isLastName)) {
    message = NO_EMPLOYEE_FOUND_MESSAGES.WITH_ONLY_NAME_FILTER;
  }

  if (isNoData === true) {
    return message;
  }
  return undefined;
}
